<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

// `contact` is the camelCased record from `contacts/getContactById` — extra phone
// numbers live in `additionalAttributes.phoneNumbers` (no dedicated DB column/migration
// for this, same free-form-jsonb pattern already used for `additionalAttributes.socialProfiles`).
const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isSaving = computed(() => uiFlags.value.isUpdating);

const isAdding = ref(false);
const newNumber = ref('');
const newLabel = ref('');

const phoneNumbers = computed(
  () => props.contact?.additionalAttributes?.phoneNumbers || []
);

const resetForm = () => {
  isAdding.value = false;
  newNumber.value = '';
  newLabel.value = '';
};

const persistPhoneNumbers = async updatedList => {
  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      additionalAttributes: {
        ...props.contact.additionalAttributes,
        phoneNumbers: updatedList,
      },
    });
    useAlert(t('KANBAN.CONTACT_LIGHTBOX.PHONE_NUMBERS.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('KANBAN.CONTACT_LIGHTBOX.PHONE_NUMBERS.ERROR_MESSAGE'));
  }
};

const confirmAddPhone = async () => {
  const number = newNumber.value.trim();
  if (!number) return;

  const alreadyExists =
    number === props.contact.phoneNumber ||
    phoneNumbers.value.some(phone => phone.number === number);
  if (alreadyExists) {
    useAlert(t('KANBAN.CONTACT_LIGHTBOX.PHONE_NUMBERS.DUPLICATE_ERROR'));
    return;
  }

  await persistPhoneNumbers([
    ...phoneNumbers.value,
    { number, label: newLabel.value.trim() },
  ]);
  resetForm();
};

const removePhone = idx => {
  const updatedList = phoneNumbers.value.filter((_, i) => i !== idx);
  persistPhoneNumbers(updatedList);
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <span class="py-1 text-sm font-medium text-n-slate-12">
      {{ t('KANBAN.CONTACT_LIGHTBOX.PHONE_NUMBERS.TITLE') }}
    </span>

    <div
      v-for="(phone, idx) in phoneNumbers"
      :key="`${phone.number}-${idx}`"
      class="flex items-center gap-2"
    >
      <span class="flex-1 text-sm text-n-slate-12">{{ phone.number }}</span>
      <span v-if="phone.label" class="text-xs text-n-slate-10">{{
        phone.label
      }}</span>
      <Button
        icon="i-lucide-x"
        size="xs"
        variant="ghost"
        color="slate"
        :disabled="isSaving"
        @click="removePhone(idx)"
      />
    </div>

    <div v-if="isAdding" class="flex flex-col gap-2">
      <PhoneNumberInput
        v-model="newNumber"
        :placeholder="
          t('KANBAN.CONTACT_LIGHTBOX.PHONE_NUMBERS.NUMBER_PLACEHOLDER')
        "
      />
      <Input
        v-model="newLabel"
        :placeholder="t('KANBAN.CONTACT_LIGHTBOX.PHONE_NUMBERS.LABEL_PLACEHOLDER')"
      />
      <div class="flex items-center gap-2">
        <Button
          size="sm"
          :label="t('KANBAN.CONTACT_LIGHTBOX.PHONE_NUMBERS.ADD')"
          :is-loading="isSaving"
          :disabled="!newNumber.trim() || isSaving"
          @click="confirmAddPhone"
        />
        <Button
          size="sm"
          variant="faded"
          color="slate"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          :disabled="isSaving"
          @click="resetForm"
        />
      </div>
    </div>
    <Button
      v-else
      size="sm"
      variant="ghost"
      icon="i-lucide-plus"
      :label="t('KANBAN.CONTACT_LIGHTBOX.PHONE_NUMBERS.ADD')"
      class="self-start"
      @click="isAdding = true"
    />
  </div>
</template>
